import React from 'react';
import IDESelector, {
  IDEItem,
  IDEKey,
} from '@hud/web-components/IDESelector/IDESelector';
import config from '../../../config';
import { FunctionData } from '@hud/portal-dtos/webapp-function.dto';
import useEnvironment from '../../../hooks/useEnvironment';
import useAnalytics from '../../../hooks/useAnalytics';
import { useBooleanFeatureFlag } from '../../../hooks/useFeatureFlags';
import { useIDECheckins } from '../../../hooks/useIDECheckins';

// configures which IDEs are available in the selector and their url prefix
const IDEPrefixMap: {
  key: IDEKey;
  urlPrefix: string;
  marketplaceUrl: string;
}[] = [
  {
    key: 'Visual_Studio_Code',
    urlPrefix: `vscode://${config.IDE_EXTENSION_ID}`,
    marketplaceUrl: 'https://marketplace.visualstudio.com/items?itemName=Hud.hud',
  },
  {
    key: 'Cursor',
    urlPrefix: `cursor://${config.IDE_EXTENSION_ID}`,
    marketplaceUrl: 'https://marketplace.visualstudio.com/items?itemName=Hud.hud',
  },
  {
    key: 'Windsurf',
    urlPrefix: `windsurf://${config.IDE_EXTENSION_ID}`,
    marketplaceUrl: 'https://marketplace.visualstudio.com/items?itemName=Hud.hud',
  },
  {
    key: 'WS',
    urlPrefix: 'hud://webstorm.navigate',
    marketplaceUrl: 'https://plugins.jetbrains.com/plugin/25174-hud',
  },
  {
    key: 'PY',
    urlPrefix: 'hud://pycharm.navigate',
    marketplaceUrl: 'https://plugins.jetbrains.com/plugin/25174-hud',
  },
  {
    key: 'PC',
    urlPrefix: 'hud://pycharmce.navigate',
    marketplaceUrl: 'https://plugins.jetbrains.com/plugin/25174-hud',
  }, // pycharm community edition
  {
    key: 'IU',
    urlPrefix: 'hud://ideaultimate.navigate',
    marketplaceUrl: 'https://plugins.jetbrains.com/plugin/25174-hud',
  }, // idea ultimate
  {
    key: 'IC',
    urlPrefix: 'hud://ideace.navigate',
    marketplaceUrl: 'https://plugins.jetbrains.com/plugin/25174-hud',
  }, // idea community edition
];

const availableIDEsIfNoData: IDEKey[] = [
  'Visual_Studio_Code',
  'Cursor',
  'Windsurf',
  'WS',
  'PY',
  'IU',
];

type IDESelectorRendererProps = {
  functionData: FunctionData;
};

export default function IDESelectorRenderer({ functionData }: IDESelectorRendererProps) {
  const { environment } = useEnvironment();
  const analytics = useAnalytics();

  // We currently dont support JB deeplink in Linux
  const showJBProducts = useBooleanFeatureFlag('enable-jb-in-ide-selector');

  const ideCheckins = useIDECheckins();
  let ideList: IDEItem[] = [];
  const noCheckinsData = Object.entries(ideCheckins.data).length === 0;

  if (noCheckinsData) {
    ideList = availableIDEsIfNoData.map((ide) => {
      const ideData = IDEPrefixMap.find((item) => item.key === ide);
      const ideItem: IDEItem = {
        key: ide,
        marketplaceUrl: ideData?.marketplaceUrl ?? '',
        isInstalled: false,
        alwaysShow: true,
      };
      return ideItem;
    });
  } else {
    ideList = IDEPrefixMap.map((ideData) => {
      const ideItem: IDEItem = {
        key: ideData.key,
        marketplaceUrl: ideData.marketplaceUrl,
        isInstalled: Object.keys(ideCheckins.data).includes(ideData.key),
      };
      return ideItem;
    });
  }

  function filterOutJB(ide: IDEItem) {
    const ideData = IDEPrefixMap.find((item) => item.key === ide.key);
    return showJBProducts || !ideData?.marketplaceUrl.includes('jetbrains');
  }

  ideList = ideList.filter(filterOutJB);
  const handleIDEClick = (ideKey: IDEKey, element: string) => {
    const ideInstalled = ideList.find((ide) => ide.key === ideKey)?.isInstalled ?? false;

    const id = functionData.id;
    const repoName = functionData.repositoryName;
    const filePath = functionData.filePath;
    const lineNumber = functionData.lineNumber;
    const functionName = functionData.name;
    const environmentSearchParam = environment?.name
      ? `&environment=${environment.name}`
      : '';
    const identity = functionData.identity ? JSON.stringify(functionData.identity) : '';
    const encodedParams = encodeURIComponent(
      JSON.stringify({
        repo: repoName,
        file: filePath,
        line: lineNumber,
        function: functionName,
        id: id,
        environment: environment?.name,
        identity: identity,
      }),
    );

    const urlPrefix = IDEPrefixMap.find((ide) => ide.key === ideKey)?.urlPrefix;
    // Note: We keep the original params for now for backwards compatibility, but they should be removed in the future.
    const handlerURL = `${urlPrefix}?repo=${repoName}&file=${filePath}&line=${lineNumber}&function=${functionName}${environmentSearchParam}&params=${encodedParams}`;
    analytics.capture('function_page_deep_link_clicked', {
      ide: ideKey,
      function_id: id,
      function_name: functionData.name,
      link: handlerURL,
      environment: environment?.name,
      element,
      ideInstalled,
    });

    if (ideInstalled) {
      window.open(handlerURL);
    }
  };

  const handleShowMoreClick = () => {
    analytics.capture('function_page_ide_selector_show_more_clicked', {});
  };

  return (
    <IDESelector
      onClickIDE={handleIDEClick}
      onClickShowMore={handleShowMoreClick}
      ideList={ideList}
    />
  );
}
